import { PublicKey, SystemProgram } from "@solana/web3.js";
import { useFormikContext } from "formik";
import { FC, useCallback } from "react";

import closeIcon from "../../../../assets/closeCircle.svg";
import {
  EMPTY_STRING,
  SOL_DECIMAL_PLACES,
} from "../../../../common/constants/common.constants";
import { ITradingApprovalGovernanceData } from "../../../../common/interfaces/club-config.interface";
import { ICreateProposalForm } from "../../../../common/interfaces/proposals.interface";
import { clubsStore } from "../../../../contexts/clubStore";
import { getDecimalsPerMint } from "../../../../utilities/helpers";
import WizardField from "../../../WizardField/WizardField";
import "./VotingThreshold.scss";

interface IVotingThresholdProps {
  removeItem?: () => void;
  index?: number;
  name: string;
  valueFrom?: number | string;
  valueTo?: number | string;
  quorumValue?: number | string;
  governance?: PublicKey;
  publickey?: PublicKey;
  onQuorumChange?: (quorum: ITradingApprovalGovernanceData) => void;
  mintSymbol: string;
  isChecked?: boolean;
  areInputsDisabled?: boolean;
  onClick?: (from: number | string) => void;
  isUpdateGovernanceProposal?: boolean;
}

enum SellPermissionTypeUpdate {
  To,
  From,
  Qurom,
}

const VotingThreshold: FC<IVotingThresholdProps> = ({
  removeItem,
  index,
  name,
  valueFrom,
  valueTo,
  quorumValue,
  publickey,
  onQuorumChange,
  areInputsDisabled,
  isUpdateGovernanceProposal,
  mintSymbol,
  isChecked,
  onClick,
}) => {
  const { values, setFieldValue } = useFormikContext<ICreateProposalForm>();
  const { treasury } = clubsStore.getState();

  const changeQuorum = useCallback(
    (e, spType: SellPermissionTypeUpdate) => {
      const governances = [...values.governanceAccounts];

      let govIndex = governances.findIndex(
        (sp) => sp.publickey.toString() === SystemProgram.programId.toString()
      );

      if (areInputsDisabled) {
        govIndex = governances.findIndex((sp) => sp.from === valueFrom);
      }

      const decimals = getDecimalsPerMint(treasury?.denominatedTokenInfo);

      switch (spType) {
        case SellPermissionTypeUpdate.From: {
          governances[govIndex] = {
            ...governances[govIndex],
            from: +e.target.value * decimals,
            isChecked: true,
            newQuorum: areInputsDisabled
              ? governances[govIndex].newQuorum
              : governances[govIndex].newQuorum === 0
              ? EMPTY_STRING
              : governances[govIndex].newQuorum,
          };
          setFieldValue("activeTradeApprovalQuorum", {
            ...values.activeTradeApprovalQuorum,
            from: +e.target.value,
          });
          break;
        }
        case SellPermissionTypeUpdate.To: {
          governances[govIndex] = {
            ...governances[govIndex],
            to: +e.target.value * decimals,
            isChecked: true,
          };
          setFieldValue("activeTradeApprovalQuorum", {
            ...values.activeTradeApprovalQuorum,
            to: +e.target.value,
          });
          break;
        }
        case SellPermissionTypeUpdate.Qurom: {
          governances[govIndex] = {
            ...governances[govIndex],
            newQuorum: +e.target.value,
            isChecked: true,
          };
          break;
        }
      }

      setFieldValue("governanceAccounts", governances);
    },
    [values]
  );

  return (
    <div
      className={
        !isChecked
          ? "voting-threshold"
          : "voting-threshold voting-threshold--checked"
      }
      onClick={() => (onClick ? onClick(quorumValue!) : () => {})}
    >
      {removeItem && (
        <img src={closeIcon} alt="remove item" onClick={removeItem} />
      )}

      {isUpdateGovernanceProposal ? (
        <div className="voting-threshold__change-club-config-quorum">
          <input
            onChange={(e) => changeQuorum(e, SellPermissionTypeUpdate.From)}
            className="wizard-field"
            disabled={areInputsDisabled}
            type="number"
            placeholder="From"
            value={
              areInputsDisabled
                ? +valueFrom! /
                  getDecimalsPerMint(treasury?.denominatedTokenInfo)
                : valueFrom
            }
          />
          <input
            onChange={(e) => changeQuorum(e, SellPermissionTypeUpdate.To)}
            className="wizard-field"
            disabled={areInputsDisabled}
            placeholder="To"
            value={
              areInputsDisabled
                ? +valueTo! / getDecimalsPerMint(treasury?.denominatedTokenInfo)
                : valueTo
            }
            type="number"
          />
          <input
            onChange={(e) => changeQuorum(e, SellPermissionTypeUpdate.Qurom)}
            className="wizard-field"
            value={quorumValue}
            placeholder="Quorum"
          />
        </div>
      ) : (
        <div className="voting-threshold__item">
          <WizardField
            type="number"
            name={`${name}.${index}.from`}
            placeholder="0"
            label="From (>=)"
            maxDecimalsLength={SOL_DECIMAL_PLACES}
            disabled={!!valueFrom}
            persistentPlaceholder={mintSymbol}
          />
          <WizardField
            type="number"
            name={`${name}.${index}.to`}
            placeholder="0"
            label="To (<)"
            maxDecimalsLength={SOL_DECIMAL_PLACES}
            disabled={!!valueTo}
            persistentPlaceholder={mintSymbol}
          />
          <WizardField
            type="number"
            name={`${name}.${index}.approvalQuorum`}
            placeholder="0"
            label="At least"
            disabled={!isChecked && isUpdateGovernanceProposal}
            onChange={
              onQuorumChange
                ? (e) =>
                    onQuorumChange({
                      from: valueFrom!,
                      to: valueTo!,
                      newQuorum: Number(e.target.value),
                      publickey: publickey!,
                    })
                : () => {}
            }
          />
        </div>
      )}
    </div>
  );
};

export default VotingThreshold;
